/* eslint-disable react-hooks/rules-of-hooks */
/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  WitboostComponent,
  WitboostSystem,
} from '@agilelab/plugin-wb-builder-common';
import {
  DefaultCell,
  Severity,
  WbInfoTooltip,
  WbEntityRefLinksCellAdapter,
  WbFavoriteEntityButton,
  WbTagsArray,
  WbTruncatedTypographyWrapper,
  WbLink,
} from '@agilelab/plugin-wb-platform';
import { Entity } from '@mainstage/catalog-model';
import { OverflowTooltip, TableColumn } from '@mainstage/core-components';
import { humanizeEntityRef } from '@mainstage/plugin-catalog-react';
import { JsonArray, JsonObject } from '@mainstage/types';
import { Box, Chip, Theme, withStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { get } from 'lodash';
import { FC, ReactNode } from 'react';
import { resolveDomainFromSystem } from '../../utils';
import { useEntityRefLink } from './hooks/useEntityRefLink';
import { CatalogTableRow } from './types';

const CheckGreenIcon = withStyles((theme: Theme) => ({
  root: {
    color: theme.palette.success.main,
  },
}))(CheckIcon);

const FavouritesButton: FC<{
  children?: ReactNode;
  entity: Entity;
}> = ({ entity, children }) => {
  return (
    <Box
      style={{
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
      }}
    >
      <WbFavoriteEntityButton entity={entity} />
      {children}
    </Box>
  );
};

// The columnFactories symbol is not directly exported, but through the
// CatalogTable.columns field.
/** @public */
export const columnFactories = Object.freeze({
  createNameColumn(options?: {
    defaultKind?: string;
    getLink: (entity: Entity) => string;
  }): TableColumn<CatalogTableRow> {
    function formatContent(entity: Entity): string {
      return (
        entity.metadata?.title ||
        humanizeEntityRef(entity, {
          defaultKind: options?.defaultKind,
        })
      );
    }

    return {
      title: 'Name',
      field: 'resolved.name',
      highlight: true,
      customSort({ entity: entity1 }, { entity: entity2 }) {
        // TODO: We could implement this more efficiently by comparing field by field.
        // This has similar issues as above.
        return formatContent(entity1).localeCompare(formatContent(entity2));
      },
      render: ({ entity }) => {
        const e = entity as WitboostSystem;
        const title = e.spec?.mesh?.name || e.metadata?.title!;
        const defaultKind = options?.defaultKind || 'Component';
        const { formattedEntityRefTitle } = useEntityRefLink({
          entityRef: entity,
          defaultKind,
        });

        return (
          <FavouritesButton entity={entity}>
            <WbLink to={options?.getLink(entity) ?? ''} showTooltip truncate>
              {title || formattedEntityRefTitle}
            </WbLink>
          </FavouritesButton>
        );
      },
      width: '30%',
    };
  },
  createTraitNameColumn(options?: {
    defaultKind?: string;
    getLink: (entity: Entity) => string;
  }): TableColumn<CatalogTableRow> {
    function formatContent(entity: Entity): string {
      return (
        entity.metadata?.title ||
        entity.metadata?.name ||
        humanizeEntityRef(entity, {
          defaultKind: options?.defaultKind,
        })
      );
    }

    return {
      title: 'Name',
      field: 'resolved.name',
      highlight: true,
      customSort({ entity: entity1 }, { entity: entity2 }) {
        return formatContent(entity1).localeCompare(formatContent(entity2));
      },
      render: ({ entity }) => {
        const e = entity;
        const title = e.metadata?.title || e.metadata?.name;
        const defaultKind = options?.defaultKind || 'Trait';
        const { formattedEntityRefTitle } = useEntityRefLink({
          entityRef: entity,
          defaultKind,
        });

        return (
          <FavouritesButton entity={entity}>
            <WbLink to={options?.getLink(entity) ?? ''} showTooltip truncate>
              {title || formattedEntityRefTitle}
            </WbLink>
          </FavouritesButton>
        );
      },
      width: '30%',
    };
  },
  createNamePracticeShaperColumn(options?: {
    defaultKind?: string;
    getLink: (entity: Entity) => string;
  }): TableColumn<CatalogTableRow> {
    function formatContent(entity: Entity): string {
      return (
        entity.metadata?.title ||
        humanizeEntityRef(entity, {
          defaultKind: options?.defaultKind,
        })
      );
    }

    return {
      title: 'Name',
      field: 'resolved.name',
      highlight: true,
      customSort({ entity: entity1 }, { entity: entity2 }) {
        return formatContent(entity1).localeCompare(formatContent(entity2));
      },
      render: ({ entity }) => {
        const title =
          (entity.metadata.displayName as string) || entity.metadata.name;

        const defaultKind = options?.defaultKind || 'Component';
        const { formattedEntityRefTitle } = useEntityRefLink({
          entityRef: entity,
          defaultKind,
        });

        return (
          <FavouritesButton entity={entity}>
            <WbLink to={options?.getLink(entity) ?? ''} showTooltip truncate>
              {title || formattedEntityRefTitle}
            </WbLink>
          </FavouritesButton>
        );
      },
      width: '30%',
    };
  },
  createSystemColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'System',
      field: 'resolved.partOfSystemRelationTitle',
      render: ({ resolved }) => (
        <WbEntityRefLinksCellAdapter
          entityRefs={resolved.partOfSystemRelations}
          defaultKind="system"
          title={resolved.partOfSystemRelationTitle}
        />
      ),
      width: '15%',
    };
  },
  createOwnerColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Owner',
      field: 'resolved.ownedByRelationsTitle',
      render: ({ resolved }) => (
        <WbEntityRefLinksCellAdapter entityRefs={resolved.ownedByRelations} />
      ),
      width: '15%',
    };
  },
  createSpecTargetsColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Targets',
      field: 'entity.spec.targets',
      render: ({ entity }) => (
        <>
          {(entity?.spec?.targets || entity?.spec?.target) && (
            <OverflowTooltip
              text={(
                (entity!.spec!.targets as JsonArray) || [entity.spec.target]
              ).join(', ')}
              placement="bottom-start"
            />
          )}
        </>
      ),
    };
  },
  createSpecTypeColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Type',
      field: 'entity.spec.type',
      hidden: true,
      width: '15%',
    };
  },
  createSpecLifecycleColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Lifecycle',
      field: 'entity.spec.lifecycle',
    };
  },
  createMetadataDescriptionColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Description',
      field: 'entity.metadata.description',
      customSort({ entity: entity1 }, { entity: entity2 }) {
        const description1 = (
          entity1 as WitboostSystem
        )?.metadata?.description?.toLowerCase();
        const description2 = (
          entity2 as WitboostSystem
        )?.metadata?.description?.toLowerCase();
        return (description1 ? description1 : '').localeCompare(
          description2 ? description2 : '',
        );
      },
      render: ({ entity }) => (
        <WbTruncatedTypographyWrapper
          value={entity.metadata.description || ''}
        />
      ),

      width: '50%',
    };
  },
  createVersionColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Version',
      field: 'entity.spec.mesh.version',
      render: ({ entity }) => {
        const e = entity as WitboostSystem;
        return (
          <OverflowTooltip
            text={e.spec?.mesh?.version}
            placement="bottom-start"
          />
        );
      },
      width: 'auto',
    };
  },
  createProjectTypeColumn(
    availableResourceTypes?: Map<string, { displayName: string }>,
  ): TableColumn<CatalogTableRow> {
    const getLabel = (option: string) => {
      return availableResourceTypes?.get(option)?.displayName ?? '';
    };
    return {
      title: 'Project type',
      field: 'entity.spec.type',
      render: ({ entity }) => {
        const e = entity as WitboostSystem;
        return (
          <OverflowTooltip
            text={getLabel(e.spec?.type) ?? e.spec?.type}
            placement="bottom-start"
          />
        );
      },
      width: 'auto',
    };
  },
  createQualifiedNameColumn(options?: {
    getLink: (entity: Entity) => string;
  }): TableColumn<CatalogTableRow> {
    return {
      title: 'Name',
      field: 'entity.spec.mesh.name',
      highlight: true,
      customSort({ entity: entity1 }, { entity: entity2 }) {
        return (entity1 as WitboostSystem).spec?.mesh?.name.localeCompare(
          (entity2 as WitboostSystem).spec?.mesh?.name,
        );
      },
      render: ({ entity, error }) => {
        const e = entity as WitboostSystem;
        const title = e.spec?.mesh?.name || e.metadata?.title;
        const defaultKind = 'Component';
        const { formattedEntityRefTitle } = useEntityRefLink({
          entityRef: entity,
          defaultKind,
        });

        return (
          <FavouritesButton entity={entity}>
            <WbLink to={options?.getLink(entity) ?? ''} showTooltip truncate>
              {title || formattedEntityRefTitle}
            </WbLink>
            {error && (
              <WbInfoTooltip
                severity={Severity.Error}
                title={`Some errors were found. Open the "${title}" page to learn more.`}
              />
            )}
          </FavouritesButton>
        );
      },
      width: '30%',
    };
  },
  createDomainColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Domain',
      field: 'entity.spec.domain',
      customSort({ entity: entity1 }, { entity: entity2 }) {
        const domain1 = (entity1 as WitboostSystem)?.spec.domain?.toLowerCase();
        const domain2 = (
          entity2 as WitboostSystem
        )?.spec?.domain?.toLowerCase();
        return (domain1 ? domain1 : '').localeCompare(domain2 ? domain2 : '');
      },
      render: ({ entity }) => {
        const e = entity as WitboostSystem;
        return (
          <OverflowTooltip
            text={e.spec?.domain?.replace('domain:', '')}
            placement="bottom-start"
          />
        );
      },
      width: '20%',
    };
  },
  createDomainDataProductColumn(
    domains: Entity[],
  ): TableColumn<CatalogTableRow> {
    return {
      title: 'Domain',
      field: 'entity.spec.domain',
      render: ({ entity }) => {
        const e = entity as WitboostSystem | WitboostComponent;
        const resolved = resolveDomainFromSystem(e, domains);
        if (!resolved) return null;
        return (
          <WbEntityRefLinksCellAdapter
            entityRefs={[resolved.domainRelations]}
            title={resolved.title}
            defaultKind="domain"
          />
        );
      },
      width: '20%',
    };
  },
  createTagsColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Tags',
      field: 'entity.metadata.tags',
      render: ({ entity }) => (
        <WbTagsArray
          tags={
            entity?.metadata?.tags?.map(t => {
              return { tagFQN: t };
            }) || []
          }
        />
      ),
      width: '20%',
    };
  },
  createUseCaseTemplateIdColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Use Case Template IDs',
      field: 'entity.spec.useCaseTemplateId',
      render: ({ entity }) => {
        const raw = entity?.spec?.useCaseTemplateId as
          | string
          | string[]
          | undefined;
        let ids: string[];
        if (raw === undefined) {
          ids = [];
        } else if (Array.isArray(raw)) {
          ids = raw;
        } else {
          ids = [raw];
        }
        return <WbTagsArray tags={ids.map(id => ({ tagFQN: id }))} />;
      },
      width: '20%',
    };
  },
  createGroupsColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Groups',
      field: 'entity.metadata.relations',
      render: ({ entity }) => {
        return (
          <WbTagsArray
            limit={15}
            tags={
              entity?.relations
                ?.filter(r => r.type === 'memberOf')
                .map(r => {
                  return { tagFQN: r.targetRef.split('/')[1] };
                }) || []
            }
          />
        );
      },
      width: '40%',
    };
  },
  createTitleColumn(options?: {
    hidden?: boolean;
  }): TableColumn<CatalogTableRow> {
    return {
      title: 'Title',
      field: 'entity.metadata.title',
      hidden: options?.hidden,
      searchable: true,
    };
  },
  createLabelColumn(
    key: string,
    options?: { title?: string; defaultValue?: string },
  ): TableColumn<CatalogTableRow> {
    return {
      title: options?.title || 'Label',
      field: 'entity.metadata.labels',
      render: ({ entity }: { entity: Entity }) => {
        const labels: Record<string, string> | undefined =
          entity.metadata?.labels;
        const specifiedLabelValue =
          (labels && labels[key]) || options?.defaultValue;
        return (
          <>
            {specifiedLabelValue && (
              <Chip
                key={specifiedLabelValue}
                label={specifiedLabelValue}
                size="small"
                variant="outlined"
              />
            )}
          </>
        );
      },
      width: 'auto',
    };
  },
  createCreationDateColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Creation Date',
      field: 'entity.metadata.createdAt',
      render: ({ entity }: { entity: Entity }) => {
        const createdAt = entity.metadata.createdAt;
        const date = createdAt ? new Date(String(createdAt)) : null;

        return date && <>{date.toLocaleString()}</>;
      },
      width: 'auto',
    };
  },
  createUserNameColumn(options?: {
    getLink: (entity: Entity) => string;
  }): TableColumn<CatalogTableRow> {
    return {
      title: 'Name',
      field: 'entity.spec.profile.displayName',
      render: ({ entity }: { entity: Entity }) => {
        return (
          <FavouritesButton entity={entity}>
            <WbLink to={options?.getLink(entity) ?? ''} showTooltip truncate>
              <>
                {(entity?.spec?.profile as JsonObject)?.displayName ||
                  entity.metadata.name}
              </>
            </WbLink>
          </FavouritesButton>
        );
      },
      width: 'auto',
    };
  },
  createUserEmailColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Email',
      field: 'entity.spec.profile.email',
      render: ({ entity }: { entity: Entity }) => {
        const e = entity as WitboostSystem;
        const email = e.spec?.profile.email;

        return <WbTruncatedTypographyWrapper value={email} />;
      },
      width: 'auto',
    };
  },
  createReleaseSystemColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'System',
      field: 'metadata',
      render: ({ entity }: { entity: Entity }) => {
        const value =
          (entity?.metadata as JsonObject)?.projectName ||
          (entity?.metadata as JsonObject)?.dataProductName ||
          '';
        return <DefaultCell value={value} />;
      },
      width: '15%',
    };
  },
  createReleaseVersionColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Version',
      field: 'metadata.version',
      render: ({ entity }: { entity: Entity }) => {
        const value = (entity?.metadata as JsonObject)?.version || '';
        return <DefaultCell value={value} />;
      },
      width: '15%',
    };
  },
  createEnabledColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Enabled',
      field: 'spec.enabled',
      align: 'right',
      render: ({ entity }: { entity: Entity }) => {
        return entity?.spec?.enabled ? (
          <Box paddingRight="16px">
            <CheckGreenIcon />
          </Box>
        ) : (
          <></>
        );
      },
      width: '15%',
    };
  },
  createResourceIDColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Resource Id',
      field: 'spec.resourceTypeId',
      render: ({ entity }: { entity: Entity }) => {
        return entity?.spec?.resourceTypeId;
      },
      width: '15%',
    };
  },
  createTaxonomyColumn(options?: {
    kind?: string;
  }): TableColumn<CatalogTableRow> {
    return {
      title: 'Taxonomy',
      field: 'spec.belongsTo',
      render: ({ entity }: { entity: Entity }) => {
        return entity?.spec?.belongsTo ? (
          <WbEntityRefLinksCellAdapter
            entityRefs={[entity?.spec?.belongsTo as string]}
            defaultKind={options?.kind ?? 'taxonomy'}
          />
        ) : (
          <></>
        );
      },
      width: '15%',
    };
  },
  createDomainTypeColumn(options?: {
    kind?: string;
    title?: string;
    fieldPath?: string;
  }): TableColumn<CatalogTableRow> {
    const path = options?.fieldPath ?? 'spec.partOfDomain';

    return {
      title: options?.title ?? 'Domain Type',
      field: path,
      customSort({ entity: entity1 }, { entity: entity2 }) {
        const domain1 = (get(entity1, path) as string)?.toLowerCase();
        const domain2 = (get(entity2, path) as string)?.toLowerCase();

        return (domain1 ? domain1 : '').localeCompare(domain2 ? domain2 : '');
      },
      render: ({ entity }) => {
        const val = get(entity, path);
        return val ? (
          <WbEntityRefLinksCellAdapter
            entityRefs={[val as string]}
            defaultKind={options?.kind ?? 'domaintype'}
          />
        ) : (
          <></>
        );
      },
      width: '20%',
    };
  },
  createSystemTypeColumn(options?: {
    kind?: string;
  }): TableColumn<CatalogTableRow> {
    return {
      title: 'System Type',
      field: 'entity.spec.partOfSystem',

      render: ({ entity }) => {
        return entity?.spec?.partOfSystem ? (
          <WbEntityRefLinksCellAdapter
            entityRefs={[entity?.spec?.partOfSystem as string]}
            defaultKind={options?.kind ?? 'systemtype'}
          />
        ) : (
          <></>
        );
      },
      width: '20%',
    };
  },
});
